import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Row,
  Col,
  Card,
  Typography,
  Spin,
  Alert,
  Table,
  Space,
  DatePicker,
  Button,
  Select,
  Tag,
  theme,
} from 'antd';
import {
  BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { userService, companiesService, efirmaService, captureLinesService } from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const isResicoRegimen = (regimen) => {
  const r = String(regimen || '').trim().toUpperCase();
  return r === 'RESICO' || r === '626';
};

const formatCurrency = (value) => {
  const n = Number(value || 0);
  return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' });
};

const formatCurrencyRounded = (value) => {
  const n = Number(value || 0);
  const rounded = Math.round(n);
  return rounded.toLocaleString('es-MX', {
    style: 'currency',
    currency: 'MXN',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });
};

const isSameOrAfter = (value, compareTo, unit = 'day') => {
  if (!value) return false;
  if (typeof value.isSameOrAfter === 'function') return value.isSameOrAfter(compareTo, unit);
  if (typeof value.isSame === 'function' && typeof value.isAfter === 'function') {
    return value.isSame(compareTo, unit) || value.isAfter(compareTo, unit);
  }
  try {
    return dayjs(value).isSame(compareTo, unit) || dayjs(value).isAfter(compareTo, unit);
  } catch (e) {
    return false;
  }
};

const UserTaxCalculation = () => {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [summary, setSummary] = useState([]);
  const [estimation, setEstimation] = useState(null);
  const [filtersOut, setFiltersOut] = useState(null);
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(1, 'month').startOf('month'),
    dayjs().subtract(1, 'month').endOf('month'),
  ]);
  const [companyId, setCompanyId] = useState('');
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [efirmaOk, setEfirmaOk] = useState(false);
  const [efirmaLoading, setEfirmaLoading] = useState(false);
  const [captureLine, setCaptureLine] = useState(null);
  const [captureLoading, setCaptureLoading] = useState(false);
  const [syncRequired, setSyncRequired] = useState(false);
  const [syncMissing, setSyncMissing] = useState([]);
  const [syncState, setSyncState] = useState('');
  const [syncingSat, setSyncingSat] = useState(false);
  const [refreshingStatus, setRefreshingStatus] = useState(false);
  const autoSelectedRef = useRef(false);

  const availableCompanies = (companies || []).filter((c) =>
    isResicoRegimen(c?.fiscal_regimen)
  );

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    setInfo('');
    setSummary([]);
    setEstimation(null);
    setFiltersOut(null);
    try {
      const filters = {};
      if (dateRange && dateRange[0] && dateRange[1]) {
        filters.date_from = dateRange[0].format('YYYY-MM-DD');
        filters.date_to = dateRange[1].format('YYYY-MM-DD');
      }
      if (companyId) {
        filters.company_id = companyId;
      }
      const data = await userService.getInvoiceTaxCalculation(filters);
      setSummary(data.summary || []);
      setEstimation(data.estimation || null);
      setFiltersOut(data.filters || null);
      setSyncRequired(false);
      setSyncMissing([]);
    } catch (e) {
      const sync = e?.response?.data?.sync_required;
      if (sync) {
        setSyncRequired(true);
        setSyncMissing(e?.response?.data?.missing || []);
        setSyncState(e?.response?.data?.sync_state || 'missing');
        const msg = e?.response?.data?.error || 'Se requiere sincronización con el SAT';
        const state = e?.response?.data?.sync_state || '';
        if (state === 'in_progress' || state === 'missing') {
          setInfo(msg);
          setError('');
        } else {
          setError(msg);
          setInfo('');
        }
      } else {
        setError('Error al cargar el cálculo de impuestos');
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, dateRange]);

  const startOfCurrentMonth = dayjs().startOf('month');
  const dateRangeHasCurrentMonth =
    Boolean(dateRange?.[0] && isSameOrAfter(dateRange[0], startOfCurrentMonth, 'day')) ||
    Boolean(dateRange?.[1] && isSameOrAfter(dateRange[1], startOfCurrentMonth, 'day'));
  const canConsult =
    Boolean(companyId) &&
    (!selectedCompany || isResicoRegimen(selectedCompany?.fiscal_regimen)) &&
    !dateRangeHasCurrentMonth;
  const canUseTool = canConsult && efirmaOk && !syncRequired;
  const selectedDateFrom = dateRange?.[0]?.format('YYYY-MM-DD');
  const selectedDateTo = dateRange?.[1]?.format('YYYY-MM-DD');
  const lastRunMatchesSelection =
    Boolean(filtersOut?.company_id) &&
    filtersOut.company_id === companyId &&
    filtersOut.date_from === selectedDateFrom &&
    filtersOut.date_to === selectedDateTo;
  const canDownloadCaptureLine = Boolean(captureLine) && lastRunMatchesSelection && !syncRequired;

  const syncSat = async () => {
    if (!companyId || !dateRange?.[0] || !dateRange?.[1]) return;
    try {
      setSyncingSat(true);
      await userService.syncSatDocuments(companyId, {
        date_from: dateRange[0].format('YYYY-MM-DD'),
        date_to: dateRange[1].format('YYYY-MM-DD'),
      });
      setSyncRequired(false);
      setSyncMissing([]);
      setError('');
      setInfo('');
      await fetchData();
    } finally {
      setSyncingSat(false);
    }
  };

  const refreshSatStatus = async () => {
    if (!companyId || !dateRange?.[0] || !dateRange?.[1]) return;
    try {
      setRefreshingStatus(true);
      await fetchData();
    } finally {
      setRefreshingStatus(false);
    }
  };

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setCompaniesLoading(true);
        const resp = await companiesService.getCompanies();
        const list = resp?.data?.companies || [];
        setCompanies(list);
        if (!autoSelectedRef.current) {
          const firstResico = (list || []).find((c) => isResicoRegimen(c?.fiscal_regimen));
          if (firstResico?.id) {
            autoSelectedRef.current = true;
            setCompanyId(firstResico.id);
            setSelectedCompany(firstResico);
          }
        }
      } catch (e) {
        setCompanies([]);
      } finally {
        setCompaniesLoading(false);
      }
    };
    loadCompanies();
  }, []);

  useEffect(() => {
    const checkEfirma = async () => {
      if (!companyId) {
        setEfirmaOk(false);
        return;
      }
      try {
        setEfirmaLoading(true);
        const resp = await efirmaService.getEfirma(companyId);
        const records = resp?.data?.records || [];
        const ok = Array.isArray(records) && records.some((r) => r.active);
        setEfirmaOk(ok);
      } catch (e) {
        setEfirmaOk(false);
      } finally {
        setEfirmaLoading(false);
      }
    };
    checkEfirma();
  }, [companyId]);

  useEffect(() => {
    const loadCaptureLine = async () => {
      const period = dateRange?.[0]?.format('YYYY-MM');
      if (!companyId || !period) {
        setCaptureLine(null);
        return;
      }
      try {
        setCaptureLoading(true);
        const resp = await captureLinesService.getByPeriod(companyId, period);
        setCaptureLine(resp?.data || null);
      } catch (e) {
        setCaptureLine(null);
      } finally {
        setCaptureLoading(false);
      }
    };
    loadCaptureLine();
  }, [companyId, dateRange]);

  useEffect(() => {
    setSyncRequired(false);
    setSyncMissing([]);
    setInfo('');
    setError('');
    setSummary([]);
    setEstimation(null);
    setFiltersOut(null);
  }, [companyId, dateRange]);

  const trasladoData = summary.filter((item) => item.tax_type === 'traslado');
  const retencionData = summary.filter((item) => item.tax_type === 'retencion');

  const totalToPay = Math.max(
    0,
    Number(estimation?.isr_a_pagar || 0) + Number(estimation?.iva_a_pagar || 0)
  );
  const hasToPay = totalToPay > 0.009;

  const columns = [
    {
      title: 'Impuesto',
      dataIndex: 'tax_name',
      key: 'tax_name',
      render: (value, record) => (
        <span>
          {value} ({record.tax_code})
        </span>
      ),
    },
    {
      title: 'Tipo factor',
      dataIndex: 'tipo_factor',
      key: 'tipo_factor',
    },
    {
      title: 'Tasa',
      dataIndex: 'tasa_o_cuota',
      key: 'tasa_o_cuota',
      render: (value) =>
        value !== null && value !== undefined
          ? `${(Number(value) * 100).toFixed(2)}%`
          : '-',
    },
    {
      title: 'Base',
      dataIndex: 'base_total',
      key: 'base_total',
      align: 'right',
      render: (value) => Number(value).toFixed(2),
    },
    {
      title: 'Importe',
      dataIndex: 'importe_total',
      key: 'importe_total',
      align: 'right',
      render: (value) => Number(value).toFixed(2),
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space align="center">
          <BarChartOutlined style={{ fontSize: 24 }} />
          <Title level={2} style={{ margin: 0 }}>
            Cálculo de impuestos
          </Title>
        </Space>

        {info && (
          <Alert
            message={info}
            description={
              syncRequired && syncMissing?.length
                ? `Faltan solicitudes: ${syncMissing.join(', ')}`
                : null
            }
            type="info"
            showIcon
            action={
              syncRequired ? (
                syncState === 'in_progress' ? (
                  <Button size="small" onClick={refreshSatStatus} loading={refreshingStatus}>
                    Refrescar
                  </Button>
                ) : (
                  <Button size="small" onClick={syncSat} loading={syncingSat}>
                    Sincronizar SAT
                  </Button>
                )
              ) : null
            }
          />
        )}

        {error && (
          <Alert
            message={error}
            description={
              syncRequired && syncMissing?.length
                ? `Faltan solicitudes: ${syncMissing.join(', ')}`
                : null
            }
            type="error"
            showIcon
            action={
              syncRequired && syncState !== 'in_progress' ? (
                <Button size="small" onClick={syncSat} loading={syncingSat}>
                  Sincronizar SAT
                </Button>
              ) : null
            }
          />
        )}

        <Card>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} md={10}>
              <Space direction="vertical" size={4}>
                <Text>Rango de fechas</Text>
                <RangePicker
                  value={dateRange}
                  onChange={(values) => setDateRange(values)}
                  format="YYYY-MM-DD"
                  disabledDate={(current) =>
                    Boolean(current) && isSameOrAfter(current, startOfCurrentMonth, 'day')
                  }
                  style={{ width: '100%' }}
                />
                {dateRangeHasCurrentMonth ? (
                  <Alert
                    type="info"
                    showIcon
                    message="No se permite calcular el mes en curso. Selecciona un rango que termine antes del inicio del mes actual."
                  />
                ) : null}
              </Space>
            </Col>
            <Col xs={24} md={8}>
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                <Text>Empresa</Text>
                <Select
                  showSearch
                  allowClear
                  placeholder="Selecciona una empresa (RESICO 626)"
                  optionFilterProp="children"
                  value={companyId || undefined}
                  onChange={(val) => {
                    setCompanyId(val || '');
                    const comp = (companies || []).find(c => c.id === val);
                    setSelectedCompany(comp || null);
                  }}
                  loading={companiesLoading}
                  style={{ width: '100%' }}
                >
                  {availableCompanies.map((c) => (
                    <Select.Option key={c.id} value={c.id}>
                      {`${c.tax_name} (${c.tax_id}) — Régimen: ${c.fiscal_regimen}`}
                    </Select.Option>
                  ))}
                </Select>
                {selectedCompany && (
                  <Tag color="blue" style={{ marginTop: 4 }}>
                    Régimen: {selectedCompany.fiscal_regimen}
                  </Tag>
                )}
                {companyId && !efirmaLoading && !efirmaOk ? (
                  <Alert
                    type="warning"
                    showIcon
                    message="Para acceder a esta sección debes cargar tu FIEL (eFirma)."
                    action={
                      <Button size="small" onClick={() => navigate('/settings')}>
                        Ir a Configuración
                      </Button>
                    }
                  />
                ) : null}
                {!companiesLoading && availableCompanies.length === 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Esta sección solo está disponible para empresas con régimen fiscal RESICO (626)."
                  />
                )}
              </Space>
            </Col>
            <Col xs={24} md={6}>
              <Button
                type="primary"
                onClick={fetchData}
                loading={loading}
                disabled={!canUseTool}
                block
              >
                Consultar
              </Button>
              {canDownloadCaptureLine ? (
                <Button
                  style={{ marginTop: 8 }}
                  onClick={async () => {
                    try {
                      const period = dateRange?.[0]?.format('YYYY-MM');
                      const resp = await captureLinesService.downloadByPeriod(companyId, period);
                      const blob = new Blob([resp.data], { type: 'application/pdf' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `linea_captura_${companyId}_${period}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch (e) {
                      // noop UI message
                    }
                  }}
                  loading={captureLoading}
                  block
                >
                  Descargar línea de captura
                </Button>
              ) : null}
            </Col>
          </Row>
        </Card>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
            <Spin size="large" />
          </div>
        ) : (
          <>
            <Card>
              <Row gutter={[16, 16]} align="middle" justify="space-between">
                <Col xs={24} md={12}>
                  <Space direction="vertical" size={2}>
                    <Text type="secondary">Periodo</Text>
                    <Text strong style={{ fontSize: 16 }}>
                      {filtersOut?.date_from || dateRange?.[0]?.format('YYYY-MM-DD')} al {filtersOut?.date_to || dateRange?.[1]?.format('YYYY-MM-DD')}
                    </Text>
                  </Space>
                </Col>
                <Col xs={24} md={12}>
                  <div
                    style={{
                      borderRadius: 8,
                      padding: '12px 16px',
                      border: `1px solid ${hasToPay ? token.colorErrorBorder : token.colorSuccessBorder}`,
                      background: hasToPay ? token.colorErrorBg : token.colorSuccessBg,
                    }}
                  >
                    <Text style={{ color: hasToPay ? token.colorError : token.colorSuccess }}>
                      {hasToPay
                        ? 'Tienes impuestos estimados por pagar'
                        : 'No tienes impuestos estimados por pagar'}
                    </Text>
                    <div style={{ marginTop: 4, fontSize: 24, fontWeight: 600, color: hasToPay ? token.colorError : token.colorSuccess }}>
                      {formatCurrencyRounded(totalToPay)}
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      ISR a pagar + IVA a pagar
                    </Text>
                  </div>
                </Col>
              </Row>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card
                  title="ISR (RESICO)"
                  extra={<Text strong>{formatCurrency(estimation?.isr_a_pagar ?? 0)}</Text>}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    <Row justify="space-between">
                      <Text type="secondary">Ingresos (sin IVA)</Text>
                      <Text strong>{formatCurrency(estimation?.ingresos ?? 0)}</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text type="secondary">Tasa aplicada</Text>
                      <Text strong>{((Number(estimation?.tasa_isr ?? 0)) * 100).toFixed(2)}%</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text type="secondary">ISR calculado</Text>
                      <Text strong>{formatCurrency(estimation?.isr_calculado ?? 0)}</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text type="secondary">ISR retenido</Text>
                      <Text strong>{formatCurrency(estimation?.isr_retenido ?? 0)}</Text>
                    </Row>
                    <div style={{ borderTop: `1px solid ${token.colorSplit}`, paddingTop: 10 }}>
                      <Row justify="space-between">
                        <Text strong>ISR a pagar</Text>
                        <Text strong>{formatCurrency(estimation?.isr_a_pagar ?? 0)}</Text>
                      </Row>
                    </div>
                  </Space>
                </Card>
              </Col>

              <Col xs={24} lg={12}>
                <Card
                  title="IVA"
                  extra={<Text strong>{formatCurrencyRounded(estimation?.iva_a_pagar ?? 0)}</Text>}
                >
                  <Space direction="vertical" style={{ width: '100%' }} size={10}>
                    <Row justify="space-between">
                      <Text type="secondary">IVA trasladado (emitidos)</Text>
                      <Text strong>{formatCurrency(estimation?.iva_trasladado ?? 0)}</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text type="secondary">IVA acreditable (recibidos)</Text>
                      <Text strong>{formatCurrency(estimation?.iva_acreditable ?? 0)}</Text>
                    </Row>
                    <Row justify="space-between">
                      <Text type="secondary">IVA retenido</Text>
                      <Text strong>{formatCurrency(estimation?.iva_retenido ?? 0)}</Text>
                    </Row>
                    <div style={{ borderTop: `1px solid ${token.colorSplit}`, paddingTop: 10 }}>
                      <Row justify="space-between">
                        <Text strong>IVA a pagar</Text>
                        <Text strong>{formatCurrencyRounded(estimation?.iva_a_pagar ?? 0)}</Text>
                      </Row>
                    </div>
                    {Number(estimation?.iva_a_favor || 0) > 0 ? (
                      <Row justify="space-between">
                        <Text type="secondary">IVA a favor</Text>
                        <Text strong style={{ color: token.colorSuccess }}>
                          {formatCurrency(estimation?.iva_a_favor ?? 0)}
                        </Text>
                      </Row>
                    ) : null}
                  </Space>
                </Card>
              </Col>
            </Row>

            <Card>
              <Text strong>Notas</Text>
              <div style={{ marginTop: 6 }}>
                <Text type="secondary">
                  La estimación se calcula con CFDIs vigentes de tipo Ingreso (PUE). IVA acreditable se toma de CFDIs recibidos pagados y relacionados a la actividad (cuando esté habilitado).
                </Text>
              </div>
            </Card>

            <Row gutter={[16, 16]}>
              <Col xs={24} lg={12}>
                <Card title="Impuestos trasladados (detalle)">
                  <Table
                    columns={columns}
                    dataSource={trasladoData.map((item, index) => ({
                      key: `${item.tax_code}-${item.tasa_o_cuota}-${item.tax_type}-${index}`,
                      ...item,
                    }))}
                    size="small"
                    pagination={false}
                  />
                </Card>
              </Col>
              <Col xs={24} lg={12}>
                <Card title="Impuestos retenidos (detalle)">
                  <Table
                    columns={columns}
                    dataSource={retencionData.map((item, index) => ({
                      key: `${item.tax_code}-${item.tasa_o_cuota}-${item.tax_type}-${index}`,
                      ...item,
                    }))}
                    size="small"
                    pagination={false}
                  />
                </Card>
              </Col>
            </Row>
          </>
        )}
      </Space>
    </div>
  );
};

export default UserTaxCalculation;
